// src/components/imagery/TimeSlider.jsx
//
// The imagery time strip. Bottom edge of the map, always visible.
//
// ── WHAT CHANGED, AND THE ONE LINE THAT MATTERED ───────────────────────────
//
// This was a compact centred slider whose graduations came from the STACK —
// "the dates the ACTIVE layers actually represent". It now takes them from the
// CATALOG: every date the org is entitled to, stable across panning.
//
// That is the whole difference in principle, and it inverts what the control is
// for. Stack-sourced graduations answer "where can I scrub the layers I already
// have", which requires the user to have built a stack before the control says
// anything. Catalog-sourced answers "what imagery exists for this ground", which
// is the question someone looking at a blank map is actually asking.
//
// ── COVERAGE IS A VISUAL STATE, NOT A FILTER ───────────────────────────────
//
// Dates without coverage in the current view are MUTED, not removed. An entry
// that vanishes when you pan tells you nothing; an entry that greys out tells
// you the date exists and has nothing here. The second is the explanation for a
// blank map, and the strip is the only place that explanation appears — there
// is deliberately no overlay message and no fallback to another date.
//
// So the muted state has to be unmistakable rather than tasteful. Three signals
// at once: a muted label, a HOLLOW rather than filled marker, and — the
// load-bearing one — the status line below, which says in words that the
// selected date has no imagery here. Opacity alone reads as "loading" or
// "disabled", which are statements about the app; this is a statement about the
// world.
//
// ── LATEST MEANS NEWEST WITH COVERAGE HERE ─────────────────────────────────
//
// Not the newest collect. The two diverge the moment a partial collect is newer
// than a full one, which is live: 2026_03_01 is the newest and covers 721 km2
// against December's 9,886. Five kilometres apart the answer is March on one
// side of a line and December on the other. See latestWithCoverage().
//
// ── EVEN SPACING, WHICH IS ALSO WHAT IT ALREADY DID ────────────────────────
//
// Positions are by index, not by date. The previous slider was already index-
// positioned (`thumbPct = sliderValue / (dates.length - 1)`); dateToMonths was
// only ever used for snapping. Keeping it: the strip is a SELECTOR, uniform hit
// targets matter because muted entries stay clickable, and at a quarterly
// cadence proportional spacing would bunch dates until the small gaps were
// unhittable. The temporal information lives in the label.
//
// ── IT DRIVES THE CURSOR, AND ACTIVATES AT MOST ONE LAYER ──────────────────
//
// Clicking a date always calls setGlobalCursor. If NOTHING is currently
// rendering it also calls addLayer once, because a strip that explains a blank
// map while being unable to unblank it is a strange thing to hand a user: the
// cursor is intent, and with an empty stack resolveImageryStack returns zero
// specs regardless of it, so the click would otherwise change nothing visible.
//
// The layer is added in "global" time_mode — following the cursor — so every
// later click is a cursor move and the stack is not touched again. addLayer
// dedupes on (series_key, resolved date) and returns the existing layer_id, so
// a redundant call is a no-op rather than a second layer.
//
// COMPARE AND FLICKER SURVIVE THIS, and the reason is worth stating because the
// obvious worry is that they do not. Both key on layer_ids in `stack` and
// auto-exit below two entries. Going from zero layers to one does not cross
// that threshold — compare was already unreachable with an empty stack and is
// equally unreachable with one entry. The user adds a second the way they
// always did, through the IMAGERY tab.
//
// THE EXACT GUARANTEE, because "does not touch the stack" is no longer it:
// this component calls addLayer at most once, only when nothing is rendering,
// and NEVER calls removeLayer or reorderStack. Nothing it does can shrink the
// stack or change the order compare reads. A future change that removes layers
// from here would break compare and would need two-layer state of its own.
//
// ── ALWAYS VISIBLE ─────────────────────────────────────────────────────────
//
// The old component returned null below two dates. An org with one collect saw
// no control at all and no indication that imagery had a date. The strip
// renders whenever the catalog has anything; with a single date it is a
// one-entry strip that still says whether that date covers the view, which is
// the question it exists to answer.

import { useMemo, useCallback, useState, useEffect } from "react";
import { Play, Pause } from "lucide-react";
import { useImageryStackStore } from "../../stores/useImageryStackStore";
import { formatAcquisitionDate } from "../../layers/imagery/formatImageryDate";
import { isFollowingCursor } from "../../layers/imagery/effectiveDate";
import { coverageByDate, latestWithCoverage } from "../../layers/imagery/coverageAtPoint";

const PLAY_INTERVAL_MS = 1500;

/**
 * @param {object}   props
 * @param {[number,number]|null} props.center  Map centre [lng, lat]. The strip
 *   resolves coverage at the CENTRE rather than over the viewport: "latest" is
 *   a per-point question, the centre is what the user is looking at, and it is
 *   stable under small pans. See the note at the foot of coverageAtPoint.js.
 */
export default function TimeSlider({ className = "", center = null }) {
    const catalog = useImageryStackStore(s => s.catalog);
    const cursorDate = useImageryStackStore(s => s.cursor.global_date);
    const setGlobalCursor = useImageryStackStore(s => s.setGlobalCursor);
    const stack = useImageryStackStore(s => s.stack);
    const getRenderSpecs = useImageryStackStore(s => s.getRenderSpecs);
    const addLayer = useImageryStackStore(s => s.addLayer);
    const enterCompareForDates = useImageryStackStore(s => s.enterCompareForDates);
    const exitCompareMode = useImageryStackStore(s => s.exitCompareMode);
    const compareEnabled = useImageryStackStore(s => s.compareMode.enabled);

    const [playing, setPlaying] = useState(false);

    // A layer is following the cursor unless it is pinned. Via the shared
    // predicate rather than `=== "follow_cursor"`, which misses the "global"
    // default that addLayer actually writes — see effectiveDate.js.
    const hasFollower = useMemo(() => stack.some(isFollowingCursor), [stack]);

    // ── The entries: every entitled date, newest LAST so the strip reads
    // left-to-right as time. Series are flattened because the strip is about
    // dates, not products; a date offered by two series appears once.
    const entries = useMemo(() => {
        const byDate = new Map();
        for (const series of catalog.series ?? []) {
            for (const acq of series.acquisitions ?? []) {
                if (!byDate.has(acq.acquisition_date)) {
                    byDate.set(acq.acquisition_date, { series, acq });
                }
            }
        }
        return [...byDate.entries()]
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([date, v]) => ({ date, ...v }));
    }, [catalog]);

    // ── Coverage at the map centre, recomputed per move. Measured at ~47 us
    // for the current corpus, which is 0.3% of a frame; both existing
    // map.on("move") handlers in MapView are unthrottled and so is this.
    const coverage = useMemo(() => {
        if (!center) return new Map();
        const merged = new Map();
        for (const series of catalog.series ?? []) {
            for (const [date, covered] of coverageByDate(series, center)) {
                // true wins over false wins over null: a date covers if ANY
                // series offering it covers, and is only unknown if nothing
                // knew.
                const prev = merged.get(date);
                if (prev === true) continue;
                if (covered === true || prev === undefined || prev === null) {
                    merged.set(date, covered);
                }
            }
        }
        return merged;
    }, [catalog, center]);

    // The date the map is actually showing: newest WITH COVERAGE at the centre.
    const { latestDate, latestSeriesKey } = useMemo(() => {
        if (!center) return { latestDate: null, latestSeriesKey: null };
        let bestDate = null;
        let bestSeries = null;
        for (const series of catalog.series ?? []) {
            const acq = latestWithCoverage(series, center);
            if (acq && (!bestDate || acq.acquisition_date > bestDate)) {
                bestDate = acq.acquisition_date;
                bestSeries = series.series_key;
            }
        }
        return { latestDate: bestDate, latestSeriesKey: bestSeries };
    }, [catalog, center]);

    // ── PLAY SKIPS DATES WITH NO COVERAGE HERE ─────────────────────────────
    //
    // Every entitled date stays VISIBLE in the strip — that is the whole design,
    // and a muted entry is how a blank map gets explained. But play is not
    // display: it is a request to look through the imagery, and a date with no
    // pixels at this location has nothing to look at. Stepping onto it renders
    // a blank frame, waits PLAY_INTERVAL_MS, and moves on.
    //
    // At the current three collects that is a nuisance. At twenty collects with
    // four covering the view it is sixteen blank frames out of twenty — a
    // twenty-four second animation of which nineteen seconds are nothing.
    //
    // Coverage is recomputed as you pan, so the playable set changes underneath
    // a running animation. That is correct: pan somewhere only December covers
    // and play should stop having anywhere to go, which the guard below does by
    // stopping rather than by cycling one frame against itself.
    const playableDates = useMemo(
        () => entries.filter(e => coverage.get(e.date) === true).map(e => e.date),
        [entries, coverage],
    );

    useEffect(() => {
        if (!playing || playableDates.length < 2) return;
        const id = setInterval(() => {
            const cur = useImageryStackStore.getState().cursor.global_date;
            const i = playableDates.indexOf(cur);
            // indexOf returns -1 when the cursor sits on a date that is not
            // playable — the user picked a muted one, then pressed play. The
            // modulo handles it without a branch: (-1 + 1) % n === 0, so play
            // starts from the first covering date rather than stalling.
            setGlobalCursor(playableDates[(i + 1) % playableDates.length]);
        }, PLAY_INTERVAL_MS);
        return () => clearInterval(id);
    }, [playing, playableDates, setGlobalCursor]);

    useEffect(() => {
        if (!hasFollower || playableDates.length < 2) setPlaying(false);
    }, [hasFollower, playableDates.length]);

    // ── WHAT IS ACTUALLY ON THE MAP, WHICH IS NOT WHAT THE CURSOR SAYS ─────
    //
    // The cursor is intent; the stack is what renders. resolveImageryStack
    // returns ZERO specs for an empty stack regardless of the cursor, so with
    // no imagery layer enabled nothing is drawn no matter which date is
    // selected — verified, not assumed.
    //
    // This strip drives the cursor only (see the header), so it CANNOT make
    // imagery appear; the IMAGERY tab's toggle is still the thing that puts a
    // layer on the map. A status line that said "Showing Dec 2025" while the
    // stack was empty was therefore stating something false, in the one place
    // this design nominates as the authoritative explanation for a blank map.
    // It now reports the difference instead of papering over it.
    const rendering = useMemo(() => {
        const specs = getRenderSpecs();
        const visible = specs.filter(sp => sp.visible && sp.dataset_key);
        return {
            any: visible.length > 0,
            dates: new Set(visible.map(sp => sp.effective_date).filter(Boolean)),
        };
        // stack and cursorDate are the inputs resolveImageryStack reads; listing
        // them is what makes this recompute when either moves.
    }, [getRenderSpecs, stack, cursorDate, catalog]);

    // Selecting a date: move the cursor, and put a layer on the map if there
    // isn't one. The cursor is set FIRST so addLayer's dedupe resolves against
    // the date just chosen rather than the one being left — zustand's set is
    // synchronous, so the store is already updated when addLayer reads it.
    const selectDate = useCallback((date, series_key) => {
        setGlobalCursor(date);
        if (!rendering.any && series_key) {
            addLayer(series_key, { time_mode: "global" });
        }
    }, [setGlobalCursor, addLayer, rendering.any]);

    const clearPin = useCallback(() => {
        if (latestDate) selectDate(latestDate, latestSeriesKey);
    }, [latestDate, latestSeriesKey, selectDate]);

    // ── COMPARE, FROM HERE, BY DATE ────────────────────────────────────────
    //
    // The pair is the selected date and the nearest OLDER date that also covers
    // this view. That is the comparison someone actually wants — "what changed
    // since last time there was imagery here" — and it is a question only this
    // control can answer, because it is the only thing that knows both the
    // dates and their coverage.
    //
    // Skipping over dates with no coverage matters: pairing against the
    // immediately preceding ENTITLED date would, wherever that date happens not
    // to cover, put an empty pane next to a full one and call it a comparison.
    const comparePair = useMemo(() => {
        if (playableDates.length < 2) return null;
        const anchor = playableDates.includes(cursorDate)
            ? cursorDate
            : playableDates[playableDates.length - 1];
        const i = playableDates.indexOf(anchor);
        const other = i > 0 ? playableDates[i - 1] : playableDates[i + 1];
        if (!other) return null;
        const series = entries.find(e => e.date === anchor)?.series;
        return series ? { seriesKey: series.series_key, a: other, b: anchor } : null;
    }, [playableDates, cursorDate, entries]);

    const toggleCompare = useCallback(() => {
        if (compareEnabled) { exitCompareMode(); return; }
        if (comparePair) {
            enterCompareForDates(comparePair.seriesKey, comparePair.a, comparePair.b);
        }
    }, [compareEnabled, comparePair, enterCompareForDates, exitCompareMode]);

    if (!entries.length) return null;

    // What the strip is currently saying, in one sentence. This is the caption
    // that carries the muted state's meaning; the per-entry styling is the
    // index into it, not the explanation.
    const selected = entries.find(e => e.date === cursorDate) ?? null;
    const selectedCovered = selected ? coverage.get(selected.date) : undefined;
    const isLatest = !!latestDate && cursorDate === latestDate;

    // AVAILABILITY AND DISPLAY ARE DIFFERENT SENTENCES. "has imagery here" is a
    // fact about the corpus; "showing" is a fact about the map. Conflating them
    // is what made this line lie with an empty stack.
    //
    // This is now announced rather than drawn -- the per-date parenthetical
    // carries the visible signal. The `tone` variable that coloured the old
    // visible line went with it: nothing reads a colour off a sr-only node.
    let status;
    const label = selected ? formatAcquisitionDate(selected.date) : null;

    if (!center) {
        status = "Locating map view…";
    } else if (!latestDate && !selected) {
        status = "No imagery for this location";
    } else if (selected && selectedCovered === false) {
        status = `No imagery here for ${label}`;
    } else if (selected && selectedCovered == null) {
        status = `Coverage unknown for ${label}`;
    } else if (!rendering.any) {
        // Covered, but nothing is drawn yet because no imagery layer is active.
        // Points at THIS control rather than the IMAGERY tab: selecting a date
        // here now activates one. An earlier draft of this line sent the user
        // to the tab, which was true for about an hour.
        const d = label ?? formatAcquisitionDate(latestDate);
        status = `${d} has imagery here — select a date to show it`;
    } else if (selected && !rendering.dates.has(selected.date)) {
        // A layer is on, but it is pinned to a different date than the strip's
        // cursor, so moving the cursor changed nothing visible.
        status = `${label} has imagery here — the active layer is pinned to another date`;
    } else {
        const d = label ?? formatAcquisitionDate(latestDate);
        status = isLatest || !selected
            ? `Showing ${d} — the most recent imagery here`
            : `Showing ${d}`;
    }

    return (
        <div className={`pointer-events-auto ${className}`}>
            <div
                className="flex items-center gap-2 rounded-md border border-slate-700/60
                           bg-slate-900/80 backdrop-blur-sm px-2 py-1
                           shadow-lg shadow-black/40"
            >
                <button
                    onClick={() => setPlaying(p => !p)}
                    disabled={!hasFollower || playableDates.length < 2}
                    className={`shrink-0 w-4 h-4 flex items-center justify-center rounded-full transition
                        ${hasFollower && playableDates.length > 1
                            ? "text-accent-action hover:bg-accent-action/20"
                            : "text-slate-700 cursor-not-allowed"}`}
                    aria-label={playing ? "Pause" : "Play"}
                    title={!hasFollower
                        ? "Switch a layer to clock mode to animate through time"
                        : playableDates.length === 0
                            ? "No imagery covers this view"
                            : playableDates.length < 2
                                ? "Only one acquisition covers this view"
                                : playing
                                    ? "Pause"
                                    : `Play through ${playableDates.length} acquisitions covering this view`}
                >
                    {playing
                        ? <Pause size={10} strokeWidth={2.5} />
                        : <Play size={10} strokeWidth={2.5} className="ml-[1px]" fill="currentColor" />}
                </button>

                {/* The dates. Evenly spaced — a selector, not a chart. */}
                <div className="flex-1 flex items-stretch justify-between gap-1" role="listbox" aria-label="Imagery acquisitions">
                    {entries.map(({ date, series }) => {
                        const covered = coverage.get(date);
                        const hasCoverage = covered === true;
                        const unknown = covered == null;
                        const isSelected = date === cursorDate;
                        // THE PARENTHETICAL, replacing the sentence that used to
                        // sit under the strip. Four values, not three: `unknown`
                        // is its own state and must never render as "No
                        // coverage" -- a missing footprint and an empty one are
                        // different facts, and only one of them means blank map
                        // (groupFootprintsIntoSeries.js:91). latestDate is the
                        // latest date WITH coverage here, so "Latest" is a claim
                        // about this view and not about the corpus.
                        const tag = unknown
                            ? "Unknown"
                            : !hasCoverage
                                ? "No coverage"
                                : date === latestDate
                                    ? "Latest"
                                    : "Coverage";
                        return (
                            <button
                                key={date}
                                role="option"
                                aria-selected={isSelected}
                                onClick={() => selectDate(date, series.series_key)}
                                title={hasCoverage
                                    ? formatAcquisitionDate(date)
                                    : unknown
                                        ? `${formatAcquisitionDate(date)} — coverage unknown`
                                        : `${formatAcquisitionDate(date)} — no imagery at this location`}
                                className={`group flex-1 min-w-0 flex flex-row items-center justify-center gap-1.5
                                    rounded px-1 py-0.5 transition
                                    ${isSelected ? "bg-accent-action/10" : "hover:bg-slate-800/60"}`}
                            >
                                {/* Signal 2: filled marker when covered, HOLLOW when not.
                                    THE ACTION ACCENT when selected, the same
                                    colour as the strip's controls. This was
                                    teal until 2026-09-18 and accent-blue for a
                                    while before that, on the theory that data
                                    and controls should read differently; in
                                    practice the blue was not prominent enough
                                    against satellite imagery, and being the
                                    odd colour out did not make it louder.
                                    Orange is the prominent one, so that finding
                                    survives the change rather than being
                                    re-tested by it.

                                    The hollow border matches the absent
                                    label's slate-400 rather than sitting a
                                    step darker: filled-vs-hollow is what
                                    carries the meaning, so the border only
                                    has to be VISIBLE, and at 6px a darker
                                    one read as no dot at all. */}
                                <span
                                    className={`shrink-0 w-2 h-2 rounded-full border transition
                                        ${hasCoverage
                                            ? (isSelected
                                                ? "bg-accent-action border-accent-action"
                                                : "bg-fg-secondary border-fg-secondary")
                                            : "bg-transparent border-slate-400"}`}
                                />
                                {/* Signal 1: a READABILITY LADDER, not an on/off.
                                    White is "there is imagery here", blue is
                                    "and it is the one showing", slate-400 is
                                    "nothing here" -- still legible, because a
                                    date the user cannot read is not a signal,
                                    it is a gap in the strip. slate-600 was
                                    close enough to the panel to disappear. */}
                                <span
                                    className={`text-[12px] leading-tight tabular-nums truncate
                                        ${!hasCoverage
                                            ? "text-slate-400"
                                            : isSelected
                                                ? "text-accent-action font-medium"
                                                : "text-white"}`}
                                >
                                    {formatAcquisitionDate(date)}{" "}
                                    <span className={isSelected ? "opacity-80" : "opacity-70"}>
                                        ({tag})
                                    </span>
                                </span>
                            </button>
                        );
                    })}
                </div>

                <button
                    onClick={toggleCompare}
                    disabled={!compareEnabled && !comparePair}
                    className={`shrink-0 text-[11px] px-1.5 py-0.5 rounded border transition
                        ${compareEnabled
                            ? "border-accent-action/60 text-accent-action bg-accent-action/10"
                            : comparePair
                                ? "border-slate-700 text-fg-secondary hover:text-accent-action hover:border-accent-action/60"
                                : "border-slate-800 text-slate-700 cursor-not-allowed"}`}
                    title={compareEnabled
                        ? "Exit compare"
                        : comparePair
                            ? `Compare ${formatAcquisitionDate(comparePair.b)} against ${formatAcquisitionDate(comparePair.a)}`
                            : "Two dates must cover this view to compare"}
                >
                    Compare
                </button>

                {/* Clear returns to latest-with-coverage, which is the default
                    state rather than "no selection" — there is always a date
                    the map is showing, or there is no imagery here at all. */}
                <button
                    onClick={clearPin}
                    disabled={!latestDate || isLatest}
                    className={`shrink-0 text-[11px] px-1.5 py-0.5 rounded border transition
                        ${!latestDate || isLatest
                            ? "border-slate-800 text-slate-700 cursor-not-allowed"
                            : "border-slate-700 text-fg-secondary hover:text-accent-action hover:border-accent-action/60"}`}
                    title="Return to the most recent imagery covering this view"
                >
                    Latest
                </button>
            </div>

            {/* Signal 3 is now the per-date parenthetical above, so this line
                is no longer drawn -- but it is still ANNOUNCED. aria-live on a
                visually hidden node keeps the pan announcement that the visible
                line provided, and keeps the two messages the parenthetical
                cannot express: that a layer is pinned to another date, and that
                a covered date is not being shown because no layer is active.
                Dropping the node entirely would have removed both silently. */}
            <div aria-live="polite" className="sr-only">
                {status}
            </div>
        </div>
    );
}
